import React, { useState, useEffect, useCallback } from 'react';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Alert, AlertDescription } from 'components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { getUserAnalytics } from '../../../services/api';
import LoadingState from '../../shared/LoadingState';
import LeaguePerformanceTable from './LeaguePerformanceTable';

const SummaryCard = ({ label, value, subtitle, valueClassName }) => (
  <div className="rounded-token-large border border-borderGrey bg-white p-4 text-center">
    <p className="text-token-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={cn('my-1 text-token-2xl font-semibold', valueClassName)}>{value}</p>
    {subtitle && <p className="text-token-base text-muted-foreground">{subtitle}</p>}
  </div>
);

// Filter select fed by { value: '', label } options — shadcn SelectItem cannot
// take an empty-string value, so '' round-trips via the 'all' sentinel.
const FilterSelect = ({ label, value, onChange, options, width }) => (
  <div className="flex flex-col gap-1.5" style={{ width }}>
    <Label className="text-token-base font-medium text-muted-foreground">{label}</Label>
    <Select
      value={value === '' ? 'all' : value}
      onValueChange={(v) => onChange(v === 'all' ? '' : v)}
    >
      <SelectTrigger>
        <SelectValue placeholder="All" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const CSV_HEADERS = [
  'League Size', 'Total Leagues', 'DFC Users', 'Expected Avg Pos', 'DFC Avg Position',
  'Pos Improvement', 'Avg Global Rank', 'Global Rank %ile', 'DFC Avg Points',
  'League Avg Points', 'Pts Advantage', 'Pts Advantage %',
];

export default function UserAnalytics() {
  // Filter state
  const [subscriptionTiming, setSubscriptionTiming] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [scoringType, setScoringType] = useState('');
  const [minMonthsSubscribed, setMinMonthsSubscribed] = useState('');
  const [filtersDirty, setFiltersDirty] = useState(false);

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFiltersDirty(false);
    try {
      const filters = { min_league_size: 3 };
      if (subscriptionTiming) filters.subscription_timing = subscriptionTiming;
      if (subscriptionType) filters.subscription_type = subscriptionType;
      if (subscriptionStatus) filters.subscription_status = subscriptionStatus;
      if (scoringType) filters.scoring_type = scoringType;
      if (minMonthsSubscribed) filters.min_months_subscribed = Number(minMonthsSubscribed);

      const result = await getUserAnalytics(filters);
      setData(result);
    } catch (err) {
      console.error('Error fetching user analytics:', err);
      setError(err?.response?.data?.detail || err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [subscriptionTiming, subscriptionType, subscriptionStatus, scoringType, minMonthsSubscribed]);

  // Load on mount
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    fetchData();
  };

  const markDirty = (setter) => (value) => {
    setter(value);
    setFiltersDirty(true);
  };

  const summary = data?.summary;
  const rows = data?.rows || [];

  const improvementClass = summary?.overall_position_improvement > 0
    ? 'text-success'
    : summary?.overall_position_improvement < 0
      ? 'text-error'
      : undefined;

  const ptsAdvPct = summary?.overall_points_advantage_pct || 0;
  const pointsAdvClass = ptsAdvPct > 0 ? 'text-success' : ptsAdvPct < 0 ? 'text-error' : undefined;

  const handleExportCsv = () => {
    if (!rows.length) return;
    const lines = rows.map((r) => [
      r.league_size,
      r.total_leagues_global,
      r.dfc_user_count,
      r.expected_avg_position,
      r.avg_dfc_position,
      r.position_improvement,
      r.avg_global_rank,
      r.avg_global_rank_pct,
      r.avg_fpl_points_dfc,
      r.avg_fpl_points_all,
      (r.avg_fpl_points_dfc || 0) - (r.avg_fpl_points_all || 0),
      r.points_advantage_pct,
    ].map((v) => (v == null ? '' : v)).join(','));
    const csv = [CSV_HEADERS.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'league-performance.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <div className="w-full min-w-0">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-token-xl font-semibold">User Analytics</h2>
          <p className="text-token-base text-muted-foreground">
            DFC user league performance vs average
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={!rows.length}>
            <Download />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-4">
        <div className="flex flex-wrap items-end gap-4">
          <FilterSelect
            label="Subscription Timing"
            width={190}
            value={subscriptionTiming}
            onChange={markDirty(setSubscriptionTiming)}
            options={[
              { value: 'pre_season', label: 'Pre-Season (Jul-Aug)' },
              { value: 'early_season', label: 'Early Season (Sep-Oct)' },
              { value: 'mid_season', label: 'Mid Season (Nov+)' },
            ]}
          />
          <FilterSelect
            label="Subscription Type"
            width={170}
            value={subscriptionType}
            onChange={markDirty(setSubscriptionType)}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'season_pass', label: 'Season Pass' },
              { value: 'pre_season_pack', label: 'Pre-Season Pack' },
            ]}
          />
          <FilterSelect
            label="Subscription Status"
            width={160}
            value={subscriptionStatus}
            onChange={markDirty(setSubscriptionStatus)}
            options={[{ value: 'active', label: 'Active Only' }]}
          />
          <FilterSelect
            label="Scoring Type"
            width={130}
            value={scoringType}
            onChange={markDirty(setScoringType)}
            options={[
              { value: 'h', label: 'H2H' },
              { value: 'c', label: 'Classic' },
            ]}
          />
          <div className="flex w-[150px] flex-col gap-1.5">
            <Label className="text-token-base font-medium text-muted-foreground" htmlFor="ua-min-months">
              Min Months Subscribed
            </Label>
            <Input
              id="ua-min-months"
              type="number"
              min={1}
              max={12}
              placeholder="Any"
              className="h-10"
              value={minMonthsSubscribed}
              onChange={(e) => {
                setMinMonthsSubscribed(e.target.value);
                setFiltersDirty(true);
              }}
            />
          </div>

          <Button
            className="h-10"
            onClick={handleApply}
            disabled={loading}
            title={filtersDirty ? 'Filters changed since last fetch' : undefined}
          >
            {loading && <Loader2 className="animate-spin" aria-hidden="true" />}
            Apply
            {filtersDirty && !loading && (
              <span className="ml-1 inline-block h-2 w-2 rounded-full bg-warning" aria-hidden="true" />
            )}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4">
          <Alert variant="destructive" className="mb-2">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" size="sm" onClick={fetchData}>Retry</Button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && !loading && (
        <div className="mb-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
          <SummaryCard
            label="DFC Users (with leagues)"
            value={summary.total_memberships?.toLocaleString()}
            subtitle={`across ${summary.total_leagues?.toLocaleString()} leagues`}
          />
          <SummaryCard
            label="Total Leagues"
            value={summary.total_leagues?.toLocaleString()}
          />
          <SummaryCard
            label="Position Improvement"
            value={`${summary.overall_position_improvement > 0 ? '+' : ''}${summary.overall_position_improvement?.toFixed(2)} places`}
            valueClassName={improvementClass}
            subtitle="vs expected average position"
          />
          <SummaryCard
            label="Points Advantage"
            value={`${ptsAdvPct > 0 ? '+' : ''}${ptsAdvPct.toFixed(1)}%`}
            valueClassName={pointsAdvClass}
            subtitle={`DFC: ${summary.overall_avg_points_dfc?.toFixed(0)} vs All: ${summary.overall_avg_points_all?.toFixed(0)}`}
          />
        </div>
      )}

      {/* Loading */}
      {loading && <LoadingState message="Loading user analytics..." />}

      {/* Table */}
      {!loading && data && (
        <LeaguePerformanceTable rows={rows} summary={summary} />
      )}
    </div>
  );
}
